import { Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatServerDateShort } from '@/lib/dates';
import { useDossierRunProvenance } from '@/features/dossier/dossier-run-provenance';
import type { DossierResponse } from '@/features/dossier/api';
import { DeNovoRenderer } from './DeNovoRenderer';
import { EstarRenderer } from './EstarRenderer';
import { EctdRenderer } from './EctdRenderer';
import { EuCtdRenderer } from './EuCtdRenderer';
import { ctdDossierTitle } from '@/features/dossier/ctdTitle';
import { EU_MEDICINAL_LEGAL_BASES } from '@/features/dossier/eu-submission-pathways';
import { EuTdRenderer } from './EuTdRenderer';
import { EuPmsRenderer } from './EuPmsRenderer';
import { EuSscpRenderer } from './EuSscpRenderer';
import { PmaRenderer } from './PmaRenderer';
import { The510kExemptRenderer } from './510kExemptRenderer';
import { IvdrTdRenderer } from './IvdrTdRenderer';
import { IvdrPmsRenderer } from './IvdrPmsRenderer';
import { IvdrSspRenderer } from './IvdrSspRenderer';

/**
 * Dossier result renderer. Dispatches between four bespoke layouts based
 * on the schema the agent returned + the submission_type:
 *   - eSTAR PMA — submission_type === "pma", renders 26 sections
 *     (PmaRenderer). Must be checked FIRST among eSTAR-family schemas
 *     because PMA / De Novo / 510(k) all share the `section_NN_` key
 *     naming convention.
 *   - eSTAR De Novo — submission_type starts with "denovo", renders 25
 *     sections (DeNovoRenderer).
 *   - eSTAR 510(k) — detected by presence of `section_01_submission_type`
 *     when neither PMA nor De Novo, renders 22 sections per FDA Table 1
 *     ordering (EstarRenderer).
 *   - eCTD NDA / 505(b)(2) / ANDA / BLA / Biosimilar — detected by
 *     presence of `module_1_administrative`, renders 5 ICH-CTD modules
 *     (EctdRenderer). The biosimilar agent uses the SAME EctdDossier
 *     validator with biosimilar-specific content in prose fields.
 *
 * Mirrors the legacy `dossier-renderer.js`. Layout chrome (submission
 * summary card on top, Download PDF action on the heading, tool-calls panel
 * at the bottom) matches the predicate + strategy + protocol result pages.
 */

const SUBMISSION_TYPE_LABELS: Record<string, string> = {
  '510k': '510(k) Premarket Notification',
  'denovo-no-clinical': 'De Novo (without clinical data)',
  'denovo-clinical': 'De Novo (with clinical data)',
  pma: 'PMA — Premarket Approval',
  'pma-supplement': 'PMA Supplement',
  '510k-exempt': '510(k) Exempt — QMS & Technical Documentation',
  nda: 'NDA — New Drug Application',
  'nda-505b2': 'NDA — 505(b)(2) Application',
  anda: 'ANDA — Abbreviated New Drug Application',
  bla: 'BLA — Biologics License Application',
  biosimilar: 'Biosimilar (351(k))',
  // EU MDR device classes (submission_type for EU runs)
  IIa: 'EU MDR — Class IIa',
  IIb: 'EU MDR — Class IIb',
  IIb_implantable: 'EU MDR — Class IIb (Implantable)',
  III: 'EU MDR — Class III',
  // EU IVDR device classes
  A: 'EU IVDR — Class A',
  A_sterile: 'EU IVDR — Class A (Sterile)',
  B: 'EU IVDR — Class B',
  C: 'EU IVDR — Class C',
  D: 'EU IVDR — Class D',
  // EU medicinal (drug / biologic) legal bases — Directive 2001/83/EC
  'art-8-3': 'EU CTD — Art. 8(3) Full Application',
  'art-10a': 'EU CTD — Art. 10a Well-Established Use',
  'art-10b': 'EU CTD — Art. 10b Fixed Combination',
  'art-10c': 'EU CTD — Art. 10c Informed Consent',
  'art-10-1': 'EU CTD — Art. 10(1) Generic',
  'art-10-3': 'EU CTD — Art. 10(3) Hybrid',
  'art-10-4': 'EU CTD — Art. 10(4) Biosimilar',
};

/** submission_type values that denote an EU jurisdiction (MDR/IVDR device
 *  classes + medicinal legal bases). Used to jurisdiction-label the summary
 *  card eyebrow. */
const EU_SUBMISSION_TYPES = new Set([
  'IIa', 'IIb', 'IIb_implantable', 'III', 'A', 'A_sterile', 'B', 'C', 'D',
  ...EU_MEDICINAL_LEGAL_BASES,
]);

export function DossierResultRenderer({
  response,
  runId,
  generatedAt,
  generatedBy,
  showSummary = true,
  embedded = false,
  country = null,
}: {
  response: DossierResponse;
  runId: number;
  /** Run's real completion time (ISO) + operator name, from the outer run
   *  detail — threaded to the summary card so it shows WHEN it ran and WHO
   *  ran it (not the view time). */
  generatedAt?: string | null;
  generatedBy?: string | null;
  showSummary?: boolean;
  /** Embedded inside the grouped EU dossier view: drops the summary card,
   *  the title-row + per-annex "Download PDF" button, and the top margin, and
   *  hides the EU annex renderers' identity cover block — so each annex reads
   *  as a continuous body under one shared header. Non-embedded output is
   *  byte-identical. EU annexes only. */
  embedded?: boolean;
  /** The run's country — labels the summary card (NI vs EU share device classes). */
  country?: string | null;
}) {
  const dossier = response.dossier ?? {};
  const documentType = (response['document_type'] as string | null | undefined) ?? null;

  // ── IVDR (Reg 2017/746) — EU ('ivdr_*') and Northern Ireland ('ni_ivdr_*') ──
  // IVDR runs arrive with document_type 'ivdr_td' | 'ivdr_pms' | 'ivdr_ssp' (EU) or
  // 'ni_ivdr_td' | 'ni_ivdr_pms' | 'ni_ivdr_ssp' (NI). Their dossier schemas share
  // top-level keys with EU MDR schemas (s1_device_description / data_collection /
  // s1_device_and_intended_purpose), so document_type is checked FIRST —
  // before the MDR schema-key branches — to prevent misrouting.
  const ivdrKind = documentType?.replace(/^ni_/, '') ?? null;
  const isIvdr = ivdrKind?.startsWith('ivdr_') === true;
  const ivdrRegion = documentType?.startsWith('ni_') ? 'NI' : 'EU';
  // TD: document_type '(ni_)ivdr_td' + schema key s1_device_description (object)
  const isIvdrTd =
    isIvdr &&
    ivdrKind === 'ivdr_td' &&
    dossier['s1_device_description'] != null &&
    typeof dossier['s1_device_description'] === 'object';
  // PMS: document_type '(ni_)ivdr_pms' + schema key data_collection (object)
  const isIvdrPms =
    isIvdr &&
    ivdrKind === 'ivdr_pms' &&
    dossier['data_collection'] != null &&
    typeof dossier['data_collection'] === 'object';
  // SSP: document_type '(ni_)ivdr_ssp' + schema key s1_device_and_intended_purpose
  const isIvdrSsp =
    isIvdr &&
    ivdrKind === 'ivdr_ssp' &&
    dossier['s1_device_and_intended_purpose'] != null &&
    typeof dossier['s1_device_and_intended_purpose'] === 'object';

  // EU MDR Annex II TD — detected by `s1_device_description` (unique to
  // AnnexIITechnicalDoc; no US eSTAR/eCTD schema carries this key).
  // Checked AFTER IVDR gates so an IVDR TD does not fall into this branch.
  const isEuTd =
    !isIvdr &&
    dossier['s1_device_description'] != null &&
    typeof dossier['s1_device_description'] === 'object';
  // EU MDR Annex III PMS Plan — detected by `data_collection` (unique to
  // AnnexIIIPmsPlan; no US eSTAR/eCTD schema nor the EU TD schema carries
  // this top-level key). Checked immediately after EU TD. IVDR runs are
  // excluded via !isIvdr (their PMS also uses data_collection).
  const isEuPms =
    !isIvdr &&
    !isEuTd &&
    dossier['data_collection'] != null &&
    typeof dossier['data_collection'] === 'object';
  // EU MDR Art. 32 SSCP — detected by `s1_device_and_intended_purpose`
  // (unique to SscpDocument; cannot collide with EU TD's `s1_device_description`
  // nor PMS's `data_collection`). Checked immediately after EU TD + PMS.
  // IVDR SSP also uses s1_device_and_intended_purpose → excluded via !isIvdr.
  const isEuSscp =
    !isIvdr &&
    !isEuTd &&
    !isEuPms &&
    dossier['s1_device_and_intended_purpose'] != null &&
    typeof dossier['s1_device_and_intended_purpose'] === 'object';
  // PMA / De Novo / 510(k) all share the `section_NN_` key naming. The
  // submission_type discriminator runs FIRST so PMA's 26 sections + De
  // Novo's 25 sections don't get silently truncated by the 22-section
  // EstarRenderer.
  const isPma =
    !isIvdr && !isEuTd && !isEuPms && !isEuSscp && response.submission_type === 'pma';
  const isDeNovo =
    !isIvdr &&
    !isEuTd &&
    !isEuPms &&
    !isEuSscp &&
    (response.submission_type ?? '').startsWith('denovo');
  // 510(k)-exempt — no FDA submission is made; the package is the internal
  // Medical Device File + applicability determinations. Its dossier shape
  // (`determinations` / `medical_device_file`) doesn't collide with the
  // section_NN_ / module_N_ keys the other US/eCTD schemas use, but the
  // submission_type discriminator is checked FIRST here too, for the same
  // reason as PMA/De Novo above, and to exclude it from isEstar explicitly.
  const is510kExempt =
    !isIvdr &&
    !isEuTd &&
    !isEuPms &&
    !isEuSscp &&
    response.submission_type === '510k-exempt';
  // Outcome-aware heading: the top-level `exemption_outcome` field drives
  // whether the heading claims exemption. Fail-safe — anything other than
  // an exact "exempt" (incl. null/missing) is NOT exempt. LOCKSTEP with the
  // banner in The510kExemptRenderer and the PDF's `is_exempt` in
  // backend/services/dossier/estar_renderer/_510k_exempt.py.
  const is510kExemptOutcome =
    String(dossier['exemption_outcome'] ?? '').toLowerCase() === 'exempt';
  const isEstar =
    !isIvdr &&
    !isEuTd &&
    !isEuPms &&
    !isEuSscp &&
    !isPma &&
    !isDeNovo &&
    !is510kExempt &&
    dossier['section_01_submission_type'] != null &&
    typeof dossier['section_01_submission_type'] === 'object';
  const isEctd =
    !isIvdr &&
    !isEuTd &&
    !isEuPms &&
    !isEuSscp &&
    dossier['module_1_administrative'] != null &&
    typeof dossier['module_1_administrative'] === 'object';
  // IVD: the backend stamps is_ivd on the response envelope; each device
  // renderer (510(k)/De Novo/PMA) then swaps in its IVD section variants.
  const isIvd = response.is_ivd === true;
  // Heading tag: explicit "IVD" / "non-IVD" for classified device dossiers; ''
  // for legacy runs where is_ivd was never resolved. (eCTD has no IVD concept.)
  const ivdTag =
    response.is_ivd === true ? 'IVD ' : response.is_ivd === false ? 'non-IVD ' : '';

  // Embedded (grouped EU view): drop the summary card + the title-row with the
  // per-annex Download-PDF button, sit flush under the grouped heading, and
  // hide the EU annex cover block. Non-embedded output is byte-identical.
  const renderSummary = showSummary && !embedded;

  // The EU drug & biologic CTD is the one pathway whose title row sits BELOW the identity
  // block, level with Download PDF — the EU MDR Class IIa layout.
  const isEuCtd = isEctd && documentType === 'eu_ctd';
  const downloadPdfButton = (
    <Button variant="brand" size="sm" asChild>
      <a
        href={`/api/agents/dossier/runs/${runId}/pdf`}
        target="_blank"
        rel="noopener"
        title="Download as PDF"
      >
        <Download className="size-3.5" /> Download PDF
      </a>
    </Button>
  );

  return (
    <>
      {renderSummary && (
        <SubmissionSummaryCard
          response={response}
          generatedAt={generatedAt}
          generatedBy={generatedBy}
          country={country}
        />
      )}

      <section className={embedded ? '' : 'mt-6'}>
        {/* Every pathway but the EU CTD titles itself here; the CTD's row goes below its
            identity block, so it is built once and handed to the renderer. */}
        {!embedded && !isEuCtd && (
          <div className="flex items-center justify-between gap-3 mb-[14px]">
            <h2 className="text-[22px] font-bold text-heading -tracking-[0.2px] m-0">
              {isIvdrTd
                ? `${ivdrRegion} IVDR Annex II Technical Documentation`
                : isIvdrPms
                  ? `${ivdrRegion} IVDR Annex III PMS Plan`
                  : isIvdrSsp
                    ? `${ivdrRegion} IVDR Summary of Safety and Performance`
                    : isEuTd
                      ? 'EU MDR Annex II Technical Documentation'
                      : isEuPms
                        ? 'EU MDR Annex III PMS Plan'
                        : isEuSscp
                          ? 'EU MDR Art. 32 SSCP'
                          : isPma
                            ? `eSTAR ${ivdTag}PMA Dossier`
                            : isDeNovo
                              ? `eSTAR ${ivdTag}De Novo Dossier`
                              : is510kExempt
                                ? is510kExemptOutcome
                                  ? `${ivdTag}510(k)-Exempt Package`
                                  : `${ivdTag}Medical Device File — Not 510(k)-Exempt`
                                : isEstar
                                  ? `eSTAR ${ivdTag}510(k) Dossier`
                                  : isEctd
                                    ? ctdDossierTitle(documentType, response.submission_type ?? null)
                                    : 'Dossier'}
            </h2>
            <div className="flex items-center gap-2">{downloadPdfButton}</div>
          </div>
        )}

        {isIvdrTd ? (
          <IvdrTdRenderer
            dossier={dossier as Record<string, unknown>}
            submissionHeader={euCoverDetails(response)}
          />
        ) : isIvdrPms ? (
          <IvdrPmsRenderer dossier={dossier as Record<string, unknown>} />
        ) : isIvdrSsp ? (
          <IvdrSspRenderer dossier={dossier as Record<string, unknown>} />
        ) : isEuTd ? (
          <EuTdRenderer
            dossier={dossier as Record<string, unknown>}
            submissionHeader={euCoverDetails(response)}
            hideCover={embedded}
          />
        ) : isEuPms ? (
          <EuPmsRenderer
            dossier={dossier as Record<string, unknown>}
            submissionHeader={euCoverDetails(response)}
            hideCover={embedded}
          />
        ) : isEuSscp ? (
          <EuSscpRenderer
            dossier={dossier as Record<string, unknown>}
            submissionHeader={euCoverDetails(response)}
            hideCover={embedded}
          />
        ) : isPma ? (
          <PmaRenderer dossier={dossier as Record<string, unknown>} ivd={isIvd} />
        ) : isDeNovo ? (
          <DeNovoRenderer dossier={dossier as Record<string, unknown>} ivd={isIvd} />
        ) : is510kExempt ? (
          <The510kExemptRenderer dossier={dossier as Record<string, unknown>} ivd={isIvd} />
        ) : isEstar ? (
          <EstarRenderer dossier={dossier as Record<string, unknown>} ivd={isIvd} />
        ) : isEuCtd ? (
          // EU drug & biologic CTD: its own EU Module 1 (the US view's is FDA's —
          // memo M-RA-261009-BA 001), as the EU PDF does.
          <EuCtdRenderer
            dossier={dossier as Record<string, unknown>}
            legalBasis={response.submission_type ?? null}
            // The cover states both, and the PDF reads them from the run — without these the
            // page asked "Submitter must complete: drug or biologic" for a run that knows.
            productName={response.product_name ?? null}
            productType={response.product_type ?? null}
            company={
              typeof response.submission_header?.company === 'string'
                ? response.submission_header.company
                : null
            }
            headingRow={
              embedded ? undefined : (
                <div className="flex items-center justify-between gap-3 mt-6 mb-[14px]">
                  <h2 className="text-[22px] font-bold text-heading -tracking-[0.2px] m-0">
                    {ctdDossierTitle(documentType, response.submission_type ?? null)}
                  </h2>
                  <div className="flex items-center gap-2 shrink-0">{downloadPdfButton}</div>
                </div>
              )
            }
          />
        ) : isEctd ? (
          <EctdRenderer
            dossier={dossier as Record<string, unknown>}
            submissionType={response.submission_type ?? null}
          />
        ) : (
          <UnknownSchemaCard />
        )}
      </section>

      {/* ToolCallsPanel removed from user-facing UI. tool_calls remain
          persisted in DossierAgentRun.output_metadata + output_text for
          forensic + audit queries. */}
    </>
  );
}

function UnknownSchemaCard() {
  return (
    <div className="bg-white border border-[#e0e0e0] rounded-xl p-[22px] text-[14px] text-ink">
      <p className="m-0 text-muted">
        The dossier completed but did not match either the eSTAR 510(k) or eCTD
        NDA/BLA schema. Download the PDF for the full output.
      </p>
    </div>
  );
}

// ── Shared dossier provenance header (all countries) ────────────────────────

/** The ONE identity header for every dossier result page, across all countries.
 *  Shows device identity + submission provenance only — product type, pathway,
 *  WHEN it was generated, and WHO ran it, all in one box so the key facts read
 *  at a glance. Account-sourced applicant identity (company / signatory / FEI /
 *  EC REP / Notified Body / MDEL / contact) is deliberately NOT shown here: it
 *  goes stale when the customer account is edited, and it already lives in the
 *  generated PDF's submission body. ``generated`` / ``generated by`` come from
 *  the run (context in production, explicit props in tests), never the view time. */
export function DossierProvenanceHeader({
  response,
  eyebrow,
  pathwayLabel,
  generatedAt,
  generatedBy,
}: {
  response: DossierResponse;
  eyebrow: string;
  /** Jurisdiction-correct pathway/class label, computed by the caller — Canada /
   *  AU / UK use their own label fns (SUBMISSION_TYPE_LABELS carries EU framing). */
  pathwayLabel: string;
  /** Overrides (tests). Production leaves these undefined and reads from context. */
  generatedAt?: string | null;
  generatedBy?: string | null;
}) {
  const ctx = useDossierRunProvenance();
  const at = generatedAt !== undefined ? generatedAt : ctx.generatedAt;
  const by = generatedBy !== undefined ? generatedBy : ctx.generatedBy;
  return (
    <div className="bg-white border border-line rounded-2xl p-6 shadow-card">
      <div className="text-xs tracking-[0.16em] uppercase text-muted font-bold mb-1.5">
        {eyebrow}
      </div>
      <h2 className="text-[22px] font-bold text-navy m-0 -tracking-[0.2px] mb-4">
        {response.product_name?.trim() || '—'}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {response.product_type && (
          <SummaryCell label="Product type" value={response.product_type} />
        )}
        {pathwayLabel && <SummaryCell label="Submission pathway" value={pathwayLabel} />}
        <SummaryCell label="Generated" value={at ? formatServerDateShort(at) : '—'} />
        <SummaryCell label="Generated by" value={by?.trim() || '—'} />
      </div>
    </div>
  );
}

/** Pathway/class label for US + EU + NI (all framed by SUBMISSION_TYPE_LABELS).
 *  Canada / AU / UK pass their own label instead. */
function submissionTypeLabel(submissionType: string | null | undefined): string {
  return submissionType
    ? (SUBMISSION_TYPE_LABELS[submissionType] ?? submissionType)
    : '';
}

export function SubmissionSummaryCard({
  response,
  generatedAt,
  generatedBy,
  country = null,
}: {
  response: DossierResponse;
  generatedAt?: string | null;
  generatedBy?: string | null;
  /** The run's country. An NI device run stores the same MDR / IVDR class as an
   *  EU run, so only the country tells them apart. */
  country?: string | null;
}) {
  const isNi = (country ?? '').toUpperCase() === 'NI';
  const isNiIvdr = ((response['document_type'] as string | null | undefined) ?? '').startsWith('ni_ivdr_');
  const isEu =
    response.submission_type != null &&
    EU_SUBMISSION_TYPES.has(response.submission_type);
  // VF-23: 510(k)-exempt makes NO FDA submission — the package is an
  // internal Medical Device File (QMS & Technical Documentation), not a
  // "dossier". LOCKSTEP with the `is510kExempt` gate in
  // DossierResultRenderer above (both key off submission_type === '510k-exempt').
  const is510kExempt = response.submission_type === '510k-exempt';
  return (
    <DossierProvenanceHeader
      response={response}
      eyebrow={
        isNi
          ? isNiIvdr
            ? 'UK (Northern Ireland) IVDR Dossier'
            : 'UK (Northern Ireland) MDR Dossier'
          : isEu
          ? 'EU Submission Dossier'
          : is510kExempt
            ? 'US Medical Device File'
            : 'FDA Submission Dossier'
      }
      pathwayLabel={submissionTypeLabel(response.submission_type)}
      generatedAt={generatedAt}
      generatedBy={generatedBy}
    />
  );
}

// ── EU grouped dossier header ───────────────────────────────────────────────

/** The ONE shared identity header for the grouped EU dossier view (multi-annex
 *  "European Union" tab). Reuses pickHeader / pickEuHeader / SummaryCell so the
 *  cover identity (Manufacturer · EC REP · Notified Body · Basic UDI-DI ·
 *  signatory · contact) is shown once, not repeated per annex. */
export function EuDossierHeader({
  response,
  eyebrow = 'EU Submission Dossier',
}: {
  response: DossierResponse;
  /** Override for the NI-MDR case, which reuses this EU combined view but must
   *  be labeled "Northern Ireland", not "EU". */
  eyebrow?: string;
}) {
  return (
    <DossierProvenanceHeader
      response={response}
      eyebrow={eyebrow}
      pathwayLabel={submissionTypeLabel(response.submission_type)}
    />
  );
}

// ── NI (UK Northern Ireland IVDR) dossier header ───────────────────────────

/** Identity header for the NI IVDR combined dossier — analogous to
 *  EuDossierHeader but with NI-specific framing:
 *    - Eyebrow: "UK (Northern Ireland) IVDR Dossier"
 *    - Class badge: "EU IVDR 2017/746 — Class {X} · CE-UKNI"
 *    - SummaryCells identical to EuDossierHeader (Manufacturer · EU Authorised
 *      Rep (EC REP) · Notified Body · Basic UDI-DI · signatory · contact · SRN · PRRC)
 *    - Basic UDI-DI is stripped of any raw __PLACEHOLDER__ sentinel (same fix as
 *      CanadaDossierHeader) so it renders as "—" rather than a raw token. */
export function NiIvdrDossierHeader({ response }: { response: DossierResponse }) {
  return (
    <DossierProvenanceHeader
      response={response}
      eyebrow="UK (Northern Ireland) IVDR Dossier"
      pathwayLabel={submissionTypeLabel(response.submission_type)}
    />
  );
}

// ── Canada (Health Canada MDL) dossier header ───────────────────────────────

/** Health Canada class label — CA classes are I/II/III/IV and must NOT reuse
 *  SUBMISSION_TYPE_LABELS (those carry EU MDR framing, e.g. "III → EU MDR Class III"). */
function caClassLabel(t: string | null | undefined): string {
  if (!t) return '';
  const m = t.match(/^(?:ca-class-)?(i{1,3}|iv)$/i);
  return m ? `Class ${m[1]!.toUpperCase()}` : t;
}

/** The Health Canada identity header for a Canada MDL dossier — the CA analog of
 *  SubmissionSummaryCard / EuDossierHeader. Reuses pickHeader for the
 *  country-agnostic identity (company · signatory · contact) and pulls the
 *  Canada-specific fields (HC Company ID · MDEL · Canadian Representative) from
 *  ch1_regional_administrative. Placeholder values render as "—" (never raw). */
export function CanadaDossierHeader({ response }: { response: DossierResponse }) {
  return (
    <DossierProvenanceHeader
      response={response}
      eyebrow="Health Canada Submission Dossier"
      pathwayLabel={caClassLabel(response.submission_type)}
    />
  );
}

function SummaryCell({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  const text = (value ?? '').toString().trim();
  return (
    <div className="bg-[#f8f9fa] rounded-md py-2.5 px-3">
      <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-muted mb-0.5">
        {label}
      </div>
      <div className="text-[13px] text-navy">{text || '—'}</div>
    </div>
  );
}


/** The EU device cover details: the company-account header plus the run's product name. */
function euCoverDetails(response: DossierResponse): ReturnType<typeof pickEuHeader> & {
  product_name: string | null;
} {
  return { ...pickEuHeader(response), product_name: response.product_name ?? null };
}

/** Extract EU-specific header fields (ec_rep, notified_body, prrc, srn) from
 *  the top-level `submission_header` field of the dossier response. The EU
 *  fields are populated alongside the US ones in execution.py. */
function pickEuHeader(response: DossierResponse): {
  company?: string | null;
  ec_rep?: string | null;
  notified_body?: string | null;
  prrc?: string | null;
  srn?: string | null;
} {
  const raw = (response as Record<string, unknown>)['submission_header'];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {};
  return raw as {
    company?: string | null;
    ec_rep?: string | null;
    notified_body?: string | null;
    prrc?: string | null;
    srn?: string | null;
  };
}

